//
//A cache for loading and saving images in elements ready to be drawn by the canvas.
//

import { useState } from "react";
import { useImageStore } from "entity-store/image-store/use-image-store";
import { BackdropStyle, MapLabelStyle } from "./poster-project";
import { PointStyle } from "./poster-types";

interface ImageElememntCacheProps {
  isPublic: boolean;
  projectID: string;
}

export const useImageElementCache = (props: ImageElememntCacheProps) => {
  const { getImageUrl } = useImageStore();
  const [imageCache, setImageCache] = useState<Map<string, HTMLImageElement>>(
    new Map()
  );

  const cacheImageForBackdropStyle = async (backdropStyle: BackdropStyle) => {
    if (!backdropStyle.imageName) {
      return;
    }
    const url = await getImageUrl(
      backdropStyle.imageName,
      "jpeg",
      props.isPublic,
      props.isPublic
        ? "backdrops"
        : "projects/" + props.projectID + "/backdrops"
    );
    if (url) {
      loadAndCacheImage(backdropStyle.imageName, url);
    }
  };

  const cacheImageForMapLabelStyle = async (labelStyle: MapLabelStyle) => {
    if (!labelStyle.imageName) {
      return;
    }
    const url = await getImageUrl(
      labelStyle.imageName,
      "jpeg",
      props.isPublic,
      props.isPublic ? "labels" : "projects/" + props.projectID + "/labels"
    );

    if (url) {
      await loadAndCacheImage(labelStyle.imageName, url);
    }
  };

  const cacheImageForPointStyle = async (pointStyle: PointStyle) => {
    if (!pointStyle.imageName) {
      return;
    }
    const url = await getImageUrl(
      pointStyle.imageName,
      "png",
      props.isPublic,
      props.isPublic
        ? "pointIcons"
        : "projects/" + props.projectID + "/pointIcons"
    );
    if (url) {
      loadAndCacheImage(pointStyle.imageName, url);
    }
  };

  const loadAndCacheImage = async (id: string, url: string) => {
    const img = new window.Image();
    img.crossOrigin = "anonymous";
    let usingFallback = false;

    await new Promise((resolve) => {
      img.onload = () => resolve(null);
      img.onerror = () => {
        console.warn(`Image load failed for ${url}, switching to fallback.`);
        usingFallback = true;
        img.src = "/icons/UI-ICONS/CONTRAST/UI-ICON-REMOVE.png";
        img.onload = () => resolve(null);
        img.onerror = () => resolve(null);
      };
      img.src = url;
    });

    try {
      await img.decode();
    } catch (err) {
      console.warn(
        `Image decode failed for ${
          usingFallback ? "fallback" : "original"
        } image:`,
        err
      );
    }

    setImageCache((prevCache) => {
      const newCache = new Map(prevCache);
      newCache.set(id, img);
      return newCache;
    });
  };

  return {
    imageCache,
    cacheImage: loadAndCacheImage,
    cacheImageForBackdropStyle,
    cacheImageForMapLabelStyle,
    cacheImageForPointStyle,
  };
};
